import type { TFunction } from 'i18next';
/**
 * Разбор того, что человек вставил в поле пути RTSP.
 *
 * В поле ждут путь (`/Streaming/Channels/101`), а вставляют обычно ссылку
 * целиком — ровно в том виде, в каком её выдал производитель камеры или
 * прежняя программа: `rtsp://user:пароль@10.30.0.71:554/live`.
 *
 * Требовать от человека разобрать ссылку самому — перекладывать на него нашу
 * работу. Платформа хранит части порознь не из вредности: адрес живёт в узле,
 * потому что по нему же идут проверки доступности, а пароль — в хранилище
 * секретов, потому что его нельзя показывать и нужно шифровать (§10.2).
 * Поэтому ссылку разбирают здесь, а интерфейс говорит, что куда положено.
 */
export interface ParsedRtspInput {
  /** Путь вместе с параметрами запроса. Всегда начинается со слэша. */
  path: string;
  /** Хост из ссылки — его место в адресе узла. `null`, если вставили путь. */
  host: string | null;
  /** Порт из ссылки. `null` — не указан, значит 554. */
  port: number | null;
  /** Имя пользователя из ссылки — его место в учётных данных. */
  username: string | null;
  /** Был ли в ссылке пароль. Само значение никуда не переносится. */
  hasPassword: boolean;
}

const RTSP_SCHEME = /^rtsps?:\/\//i;

export function parseRtspInput(value: string): ParsedRtspInput {
  const raw = value.trim();

  if (!RTSP_SCHEME.test(raw)) {
    return {
      // Путь без ведущего слэша — самая частая опечатка, и отказывать
      // из-за неё нечестно: мы точно знаем, что человек имел в виду.
      path: raw.length === 0 ? '' : raw.startsWith('/') ? raw : `/${raw}`,
      host: null,
      port: null,
      username: null,
      hasPassword: false,
    };
  }

  let url: URL;

  try {
    url = new URL(raw);
  } catch {
    return { path: raw, host: null, port: null, username: null, hasPassword: false };
  }

  return {
    // Ссылка без пути — обычное дело: у части камер поток лежит в корне.
    path: `${url.pathname === '' ? '/' : url.pathname}${url.search}`,
    host: url.hostname.length === 0 ? null : decodeURIComponent(url.hostname),
    port: url.port === '' ? null : Number(url.port),
    username: url.username === '' ? null : decodeURIComponent(url.username),
    hasPassword: url.password !== '',
  };
}

/**
 * Что из вставленной ссылки не поместилось в поле пути и куда это девать.
 *
 * Пустая строка — всё поместилось. Текст адресован тому, кто вставил ссылку
 * и не понял, почему её не приняли целиком: §12 требует говорить, что
 * произошло и что делать.
 */
export function describeExtracted(
  parsed: ParsedRtspInput,
  nodeAddress: string | null,
  t: TFunction,
): string {
  const parts: string[] = [];

  if (parsed.host !== null) {
    parts.push(
      parsed.host === (nodeAddress ?? '')
        ? t('video.rtsp.hostAlready', { host: parsed.host })
        : t('video.rtsp.hostMove', { host: parsed.host }),
    );
  }

  if (parsed.port !== null && parsed.port !== 554) {
    parts.push(t('video.rtsp.portBelow', { port: parsed.port }));
  }

  if (parsed.username !== null || parsed.hasPassword) {
    parts.push(parsed.hasPassword ? t('video.rtsp.userAndPassword') : t('video.rtsp.user'));
  }

  if (parts.length === 0) {
    return '';
  }

  return t('video.rtsp.pathOnly', { parts: parts.join('; ') });
}
